import { useUploadFile, useUploadToS3, useUploadToSharepoint } from '@/api/queries/storage';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { AlertCircle, CheckCircle, Trash, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UploadStorageProviderType } from './types';

interface UploadButtonProps {
  recordId: string;
  objectName?: string;
  formatFileSize: (bytes: number) => string;
  onUploadSuccess?: () => void;
}

export function UploadButton({
  recordId,
  objectName,
  formatFileSize,
  onUploadSuccess,
}: UploadButtonProps) {
  const { t: tCommon } = useTranslation('common');
  const { t: tStorage } = useTranslation('storage');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<
    Record<
      string,
      {
        status: 'pending' | 'uploading' | 'success' | 'error';
        message?: string;
        progress?: number;
      }
    >
  >({});

  const uploadMutation = useUploadFile();
  const uploadToS3Mutation = useUploadToS3();
  const uploadToSharepointMutation = useUploadToSharepoint();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !recordId) return;

    try {
      // Initialize upload progress for all files
      const initialProgress: Record<
        string,
        { status: 'pending' | 'uploading' | 'success' | 'error'; message?: string }
      > = {};
      selectedFiles.forEach(file => {
        initialProgress[file.name] = { status: 'pending' };
      });
      setUploadProgress(initialProgress);

      // Step 1: Get S3 upload URLs from backend
      const uploadData = {
        files: selectedFiles.map(file => ({
          fileName: file.name,
          fileExtension: file.name.split('.').pop() || '',
          contentType: file.type || 'application/octet-stream',
        })),
        linkedEntityId: recordId,
        objectName: objectName,
      };

      const uploadResponse = await uploadMutation.mutateAsync(uploadData);

      // Step 2: Upload each file to S3
      const uploadPromises = uploadResponse.files.map(async (fileResponse, index) => {
        const file = selectedFiles[index];

        if (
          !fileResponse.success ||
          !fileResponse.uploadUrls ||
          fileResponse.uploadUrls.length === 0
        ) {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: {
              status: 'error',
              message: fileResponse.error || 'Failed to get upload URL',
            },
          }));
          return;
        }

        try {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'uploading', progress: 0 },
          }));

          // Track progress for each upload URL
          const uploadUrls = fileResponse.uploadUrls;
          const urlProgressMap = new Map<number, number>();
          uploadUrls.forEach((_, index) => {
            urlProgressMap.set(index, 0);
          });

          const onProgress = (urlIndex: number, progress: number) => {
            // Update progress for this specific URL
            urlProgressMap.set(urlIndex, progress);

            // Calculate combined progress as average of all URLs
            const totalProgress = Array.from(urlProgressMap.values()).reduce(
              (sum, p) => sum + p,
              0
            );
            const averageProgress = totalProgress / uploadUrls.length;

            setUploadProgress(prev => ({
              ...prev,
              [file.name]: {
                status: 'uploading',
                progress: Math.round(averageProgress),
              },
            }));
          };

          // Upload to all URLs in parallel
          const uploadPromises = uploadUrls.map(async (uploadUrl, urlIndex) => {
            switch (uploadUrl.providerType) {
              case UploadStorageProviderType.S3:
                return uploadToS3Mutation.mutateAsync({
                  file,
                  uploadUrl: uploadUrl.url,
                  onProgress: progress => onProgress(urlIndex, progress),
                });
              case UploadStorageProviderType.SHAREPOINT:
                return uploadToSharepointMutation.mutateAsync({
                  file,
                  uploadUrl: uploadUrl.url,
                  onProgress: progress => onProgress(urlIndex, progress),
                });
              default:
                throw new Error(`Unsupported upload provider type: ${uploadUrl.providerType}`);
            }
          });

          // Wait for all uploads to complete
          await Promise.all(uploadPromises);

          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'success', progress: 100 },
          }));

          toast.success(tStorage('successfullyUploaded', { fileName: file.name }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : tStorage('uploadFailed');
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: {
              status: 'error',
              message: errorMessage,
            },
          }));

          toast.error(
            tStorage('uploadFailedFile', {
              fileName: file.name,
              error: errorMessage,
            })
          );
        }
      });

      await Promise.all(uploadPromises);

      // Check if all uploads were successful by counting successful uploads
      const successfulCount = Object.values(uploadProgress).filter(
        p => p.status === 'success'
      ).length;

      if (successfulCount === selectedFiles.length) {
        const fileCount = selectedFiles.length;
        setSelectedFiles([]);
        setIsPopoverOpen(false);
        setUploadProgress({});
        toast.success(tStorage('successfullyUploadedMultiple', { count: fileCount }));
        onUploadSuccess?.();
      }
    } catch (error) {
      toast.error(tStorage('uploadFailed'), { richColors: true });
      console.error('Upload failed:', error);
      setUploadProgress({});
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setIsPopoverOpen(false);
    setUploadProgress({});
  };

  const isUploading =
    uploadMutation.isPending ||
    uploadToS3Mutation.isPending ||
    uploadToSharepointMutation.isPending;
  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button className="border-0 shadow-sm">
          <Upload className="h-4 w-4 mr-2" />
          {tStorage('upload')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">{tStorage('uploadFiles')}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-6 w-6 p-0"
              data-testid="file-upload-close-button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-3">
            {selectedFiles.length === 0 && (
              <p className="text-sm text-gray-600">{tStorage('selectFilesMessage')}</p>
            )}

            <div className="relative">
              <Button
                variant="outline"
                className="w-full hover:bg-gray-50"
                onClick={() => document.getElementById('file-input')?.click()}
              >
                {selectedFiles.length === 0 ? tStorage('chooseFiles') : tStorage('addMoreFiles')}
              </Button>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id="file-input"
                data-testid="file-input"
              />
            </div>

            {selectedFiles.length > 0 && (
              <>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {selectedFiles.map((file, index) => {
                    const progress = uploadProgress[file.name];
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            {progress && (
                              <>
                                {progress.status === 'pending' && (
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                                )}
                                {progress.status === 'uploading' && (
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                                )}
                                {progress.status === 'success' && (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                                {progress.status === 'error' && (
                                  <AlertCircle className="h-4 w-4 text-red-600" />
                                )}
                              </>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          {progress?.status === 'uploading' && progress.progress !== undefined && (
                            <div className="mt-2">
                              <ProgressBar progress={progress.progress} />
                              <p className="text-xs text-gray-500 mt-1">
                                {tCommon('format.percentage', {
                                  percent: progress.progress / 100,
                                  formatParams: {
                                    percent: { style: 'percent' },
                                  },
                                })}
                              </p>
                            </div>
                          )}
                          {progress?.status === 'error' && progress.message && (
                            <p className="text-xs text-red-600 mt-1">{progress.message}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(index)}
                          className="h-6 w-6 ml-2 text-gray-500 hover:text-red-500"
                          disabled={progress?.status === 'uploading'}
                          aria-label={tStorage('removeFile')}
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUpload}
                    disabled={
                      isUploading ||
                      Object.values(uploadProgress).some(p => p.status === 'uploading')
                    }
                    className="flex-1 text-white border-0"
                  >
                    {isUploading ||
                    Object.values(uploadProgress).some(p => p.status === 'uploading')
                      ? tStorage('uploading')
                      : tStorage('uploadMultipleFiles', { count: selectedFiles.length })}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={
                      isUploading ||
                      Object.values(uploadProgress).some(p => p.status === 'uploading')
                    }
                  >
                    {tStorage('cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
